import { useVisibilityController } from '@frontend/react';
import type { CreateDraftTaskStep } from '@frontend/react-domain';
import { useCreateDraftTask } from '@frontend/react-domain';
import { ConfirmationModal, Icon, Text, XStack, YStack } from '@frontend/web-react';
import { useDeleteDraftTaskMutation } from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';
import { useNavigate } from '@tanstack/react-router';
import { FeedbackButton } from '../../Navigation/Headers/FeedbackButton';

export const DraftCreationHeader = ({ currentStep }: { currentStep: MaybeUndefined<CreateDraftTaskStep> }) => {
  const { state } = useCreateDraftTask();
  const [
    isDeleteDraftConfirmationModalOpen,
    { close: closeDeleteDraftConfirmationModal, open: openDeleteDraftConfirmationModal },
  ] = useVisibilityController();
  const [deleteDraftTask, { isLoading: isDeleteDraftTaskLoading }] = useDeleteDraftTaskMutation();
  const navigate = useNavigate();

  const handleBack = () => navigate({ to: '/app/home' });

  const deleteDraft = async (draftTaskId: string) =>
    handleMutation({
      mutation: async () => deleteDraftTask({ draftTaskId }),
      onSuccess: () => {
        closeDeleteDraftConfirmationModal();
        navigate({ to: '/app/home' });
      },
      successMessage: 'Draft deleted',
    });
  return (
    <YStack className="w-full px-lg py-md">
      <XStack alignItems="center" justifyContent="between" px="sm">
        <YStack className="bg-neutral-100" p="xs" radius="full">
          <Icon accessibilityLabel="Go back" name="IconArrowLeft" onClick={handleBack} />
        </YStack>
        {state.draftTaskEntity ? (
          <XStack>
            <FeedbackButton className="mr-sm" draftId={state.draftTaskEntity.id} />
            <Icon
              accessibilityLabel="Delete draft"
              // eslint-disable-next-line tailwindcss/no-arbitrary-value
              className="bg-neutral-100 items-center justify-center size-[36px] rounded-full"
              color="danger"
              enableBackgroundColor={false}
              name="IconTrash"
              onClick={openDeleteDraftConfirmationModal}
              stopPropagation={false}
            />
          </XStack>
        ) : null}
      </XStack>
      {currentStep !== 'description' && (
        <YStack alignItems="center" className="cursor-pointer" px="md">
          {state.description ? (
            // eslint-disable-next-line tailwindcss/no-arbitrary-value
            <YStack alignItems="center" className="max-w-[80%]" gapY="md">
              <Text className="line-clamp-2 font-medium" type="p-md">
                {state.description}
              </Text>
              {/* TODO: How do we plan to display attachments? */}
              {/* {state.attachments.length > 0 && (
                <XStack alignItems="center" gap="xs">
                  <Icon name="IconFile" size={16} />
                  <Text type="captionBold">{state.attachments.length}</Text>
                </XStack>
              )} */}
            </YStack>
          ) : null}
        </YStack>
      )}
      <ConfirmationModal
        iconName="IconAlertCircleFilled"
        iconProps={{ color: 'danger' }}
        isOpen={isDeleteDraftConfirmationModalOpen}
        isSubmitLoading={isDeleteDraftTaskLoading}
        onClickSubmit={() => deleteDraft(state.draftTaskEntity!.id)}
        onClose={closeDeleteDraftConfirmationModal}
        submitLabel="Delete"
        subtitle="Are you sure you want to delete this draft?"
        title="Delete draft?"
        variant="destructive"
      />
    </YStack>
  );
};
